import { useEffect, useState } from 'react'
import type { JSX } from 'react'
import { useLocation, useParams, useNavigate } from 'react-router-dom'

import { useQuery } from '@apollo/client/react'

import { Dialog } from '@buffer-mono/popcorn'

import { graphql } from '~publish/graphql'
import { IdeaCard } from '~publish/pages/Create/components/IdeaCard'
import { IdeaCard_Idea } from '~publish/pages/Create/components/IdeaCard/fragments/IdeaCard'

import { IdeaErrorDialog } from './IdeaErrorDialog'

export const GetIdea = graphql(
  /* GraphQL */ `
    query GetIdea($input: IdeaIdInput!) {
      idea(input: $input) {
        __typename
        ... on Idea {
          id
          ...IdeaCard_Idea
        }
        ... on CoreWebAppCommonError {
          message
        }
      }
    }
  `,
  [IdeaCard_Idea],
)

/**
 * Component used to open an idea by ID, rendering an IdeaCard as a detail dialog.
 * Renders when corresponding route is matched.
 *
 * It will attempt to fetch the idea with the provided ideaId, returning it from cache first if available.
 */
export function IdeaDetailsDialog(): JSX.Element | null {
  const { ideaId } = useParams<{ ideaId: string; id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { data, loading } = useQuery(GetIdea, {
    variables: {
      input: {
        id: ideaId ?? '',
      },
    },
    fetchPolicy: 'cache-first',
  })

  const [isOpen, setIsOpen] = useState(false)

  const handleOpenChange = (open: boolean): void => {
    setIsOpen(open)
    if (!open) {
      /**
       * When this modal is closed, we want to navigate back to the original page
       * that opened the composer to ensure a seamless experience.
       * The '..' path will navigate up one level in the route tree.
       * (This is based on the component routing levels, not literal path segments)
       *
       * Keeping the `search` (query params) is a bit of a hack, but it prevents
       * re-rendering the /create page, since without params the page will auto-add
       * the ?view=board and thus cause a flash/re-render + resetting scroll,
       * which is not what we want.
       */
      navigate({
        pathname: '..',
        search: location.search,
      })
    }
  }

  useEffect(() => {
    if (data?.idea) {
      setIsOpen(true)
    }
  }, [data?.idea])

  if (loading) {
    return <></>
  }

  if (data?.idea?.__typename === 'CoreWebAppCommonError') {
    return <IdeaErrorDialog error={data?.idea} />
  }

  if (data?.idea?.__typename !== 'Idea' || !data?.idea) {
    handleOpenChange(false)
    return null
  }

  const idea = data.idea

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <Dialog.Content style={{ padding: 0 }} size="large">
        <IdeaCard idea={idea} titleAs="h2" />
      </Dialog.Content>
    </Dialog>
  )
}
